/**
 * Lấy tên file từ header Content-Disposition.
 * Ưu tiên dạng RFC 5987 `filename*=UTF-8''...` (tên có dấu), sau đó mới tới `filename="..."`.
 * Lưu ý: khác origin thì BE phải expose header này (Access-Control-Expose-Headers), nếu không sẽ là undefined.
 */
export function getFilenameFromContentDisposition(contentDisposition?: string): string | undefined {
  if (!contentDisposition) return undefined;

  const encoded = /filename\*\s*=\s*(?:[\w-]+)?'[^']*'([^;]+)/i.exec(contentDisposition);
  if (encoded?.[1]) {
    try {
      return decodeURIComponent(encoded[1].trim().replace(/^"|"$/g, ''));
    } catch {
      // Encode sai thì thử tiếp dạng filename= thường
    }
  }

  const plain = /filename\s*=\s*"?([^";]+)"?/i.exec(contentDisposition);
  return plain?.[1]?.trim() || undefined;
}

export function triggerBlobDownload(blob: Blob, defaultFilename: string, contentDisposition?: string) {
  const filename = getFilenameFromContentDisposition(contentDisposition) || defaultFilename;
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  a.remove();
}

const pickErrorMessage = (body: any): string | undefined => {
  const message = body?.error ?? body?.detail ?? body?.message;
  return typeof message === 'string' && message.trim() ? message : undefined;
};

/**
 * Đọc message lỗi từ response (`{"error": "..."}`, `{"detail": "..."}` của DRF...), kể cả khi body là Blob.
 * Thử JSON.parse thẳng nội dung, không dựa vào Content-Type do server set.
 */
export async function readErrorMessage(data: any): Promise<string | undefined> {
  if (!(data instanceof Blob)) return pickErrorMessage(data);
  try {
    return pickErrorMessage(JSON.parse(await data.text()));
  } catch {
    return undefined;
  }
}

// File .xlsx là file ZIP → luôn bắt đầu bằng chữ ký "PK\x03\x04"
const XLSX_SIGNATURE = [0x50, 0x4b, 0x03, 0x04];

export type XlsxBlobCheck = { ok: boolean; message?: string };

/**
 * Kiểm tra blob tải về có thật sự là file .xlsx không, dựa vào nội dung (chữ ký ZIP) chứ không dựa vào header.
 * Với responseType 'blob', body lỗi (JSON, text, HTML...) trả về 200 cũng bị bọc thành Blob;
 * khi đó trả về ok: false kèm message lỗi đọc được (nếu có) để toast, thay vì tải về 1 file .xlsx hỏng.
 */
export async function checkXlsxBlob(data: unknown): Promise<XlsxBlobCheck> {
  if (!(data instanceof Blob)) return { ok: false, message: pickErrorMessage(data) };
  try {
    const head = new Uint8Array(await data.slice(0, XLSX_SIGNATURE.length).arrayBuffer());
    if (XLSX_SIGNATURE.every((byte, i) => head[i] === byte)) return { ok: true };
  } catch {
    // Không đọc được nội dung → coi như không phải file hợp lệ
  }
  return { ok: false, message: await readErrorMessage(data) };
}
